import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useMemo } from 'react';
import { createNavigationHelper, parseUrlParams } from '../utils/navigation';
import type { UrlParams } from '../types';

/**
 * 导航助手Hook
 * 提供便捷的导航方法和URL参数处理
 */
export const useNavigationHelper = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // 创建导航助手
  const navigationHelper = useMemo(() => {
    return createNavigationHelper(navigate);
  }, [navigate]);

  // 解析当前URL参数
  const currentParams = useMemo(() => {
    return parseUrlParams(searchParams);
  }, [searchParams]);

  /**
   * 保持当前URL参数并导航到新路径
   * @param path 新路径
   * @param additionalParams 额外参数
   */
  const navigateWithParams = (path: string, additionalParams?: UrlParams) => {
    const mergedParams = { ...currentParams, ...additionalParams };
    const helper = createNavigationHelper(navigate);
    
    if (path.includes('/templates/') && !path.includes(':')) {
      // 模板详情页
      const templateId = path.split('/templates/')[1];
      helper.goToTemplateDetail(templateId, mergedParams);
    } else if (path === '/templates') {
      helper.goToTemplateList(mergedParams);
    } else {
      // 通用导航
      const searchParamsStr = new URLSearchParams();
      Object.entries(mergedParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          searchParamsStr.append(key, String(value));
        }
      });
      const queryString = searchParamsStr.toString();
      const fullPath = queryString ? `${path}?${queryString}` : path;
      navigate(fullPath);
    }
  };

  /**
   * 更新当前页面的URL参数
   * @param params 要更新的参数
   * @param replace 是否替换历史记录
   */
  const updateUrlParams = (params: UrlParams, replace = false) => {
    const mergedParams = { ...currentParams, ...params };
    const searchParamsStr = new URLSearchParams();
    
    Object.entries(mergedParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParamsStr.append(key, String(value));
      }
    });
    
    const queryString = searchParamsStr.toString();
    const newPath = queryString ? `${location.pathname}?${queryString}` : location.pathname;
    
    navigate(newPath, { replace });
  };

  /**
   * 清除指定的URL参数
   * @param paramKeys 要清除的参数键
   */
  const clearUrlParams = (paramKeys: string[]) => {
    const newParams = { ...currentParams };
    paramKeys.forEach(key => {
      delete newParams[key];
    });
    updateUrlParams(newParams, true);
  };

  /**
   * 获取指定参数的值
   * @param key 参数键
   * @returns 参数值
   */
  const getUrlParam = (key: string): string | number | boolean | undefined => {
    return currentParams[key];
  };

  return {
    // 基础导航方法
    ...navigationHelper,
    
    // 带参数的导航方法
    navigateWithParams,
    updateUrlParams,
    clearUrlParams,
    getUrlParam,
    
    // 当前状态
    currentPath: location.pathname,
    currentParams,
    currentSearch: location.search,
    
    // 便捷的判断方法
    isTemplatePage: location.pathname.startsWith('/templates'),
    isTemplateListPage: location.pathname === '/templates',
    isTemplateDetailPage: /^\/templates\/[^/]+$/.test(location.pathname),
    isTemplateCreatePage: location.pathname === '/templates/create',
    isTemplateEditPage: /^\/templates\/[^/]+\/edit$/.test(location.pathname)
  };
}; 